import { Box, Flex, Text } from "@chakra-ui/react"
import React, { useEffect } from "react"
import { Logger } from "lib/logger"
import { VideoPlayer } from "components/VideoPlayer"
import { Link } from "components/Link"
import Image from "next/image"
import { withErrorBoundary } from "react-error-boundary"
import { dispatch } from "lib/events"
import { useInView } from "react-intersection-observer"
import type { UgcType } from "types"
import { colours } from "@shared/ui.design-system"

type Props = {
  ugc: UgcType
}

const UCGVideoPlayer: React.FC<Props> = ({ ugc }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 1,
  })

  useEffect(() => {
    if (inView) {
      dispatch("page element viewed", {
        target: "PDP UGC Video",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  const sources = [
    {
      src: ugc.ugcVideoLinkHls ?? "",
      type: "application/x-mpegURL",
    },
    {
      src: ugc.ugcVideoLinkDash ?? "",
      type: "application/dash+xml",
    },
  ]

  const profileImage = ugc.ugcProfilePicture?.thumbnails?.size_240

  return (
    <Box
      id="community"
      scrollMarginTop={{ base: "110px", lg: "150px" }}
      ref={ref}
    >
      <Box
        mt={{ base: "40px", lg: "80px" }}
        w="full"
        background={colours.secondary.soft}
      >
        <Box
          margin="auto"
          maxW={{ base: "100%", lg: "25rem" }}
          css={{
            // styles to fit video to screen on tablets
            ".video-js.vjs-fluid,.video-js.vjs-16-9,.video-js.vjs-4-3,video.video-js,video.vjs-tech":
              {
                maxHeight: "100vh !important",
                position: "relative",
                width: "100%",
                height: "auto !important",
                maxWidth: "100% !important",
                paddingTop: "0 !important",
                lineHeight: 0,
              },
            ".vjs-control-bar": {
              lineHeight: 1,
            },
          }}
        >
          {profileImage && ugc.ugcUsername && (
            <Link
              href={ugc.ugcProfileLink ?? ""}
              target="_blank"
              title={`${ugc.ugcUsername} instagram post`}
            >
              <Flex
                background={colours.secondary.light}
                align="center"
                justify="left"
                border={`1px solid ${colours.secondary.strong}`}
                padding="4px 14px"
              >
                <Image
                  style={{ borderRadius: "50%" }}
                  src={profileImage ?? ""}
                  width="35"
                  height="35"
                  alt={`${ugc.ugcUsername} instagram profile picture`}
                />

                <Text as="span" mt="0" ml="-1" variant="book1">
                  {ugc.ugcUsername ?? ""}
                </Text>
              </Flex>
            </Link>
          )}

          <VideoPlayer
            autoPlayWhenScrolledIntoView
            muted
            mx="auto"
            w="full"
            videoPosition="PDP UCG Video"
            description={ugc.ugcDescription ?? ""}
            options={{
              html5: {
                nativeTextTracks: false,
              },
              playsinline: true,
              autoplay: false,
              controls: true,
              responsive: true,
              fluid: true,
              aspectRatio: "9:16",
              sources,
            }}
            videoProps={{
              title: ugc.ugcTitle ?? "video title",
              "aria-label": ugc.ugcTitle ?? "aria label",
              className:
                "video-js vjs-big-play-centered vjs-show-big-play-button-on-pause",
            }}
            coverImageContainerProps={{
              backgroundColor: "black",
            }}
            coverImageProps={{
              src: ugc.ugcCoverImage?.thumbnails?.size_OTF_BETA ?? "",
              title: ugc?.ugcCoverImage?.description ?? "cover image title",
              alt: ugc?.ugcCoverImage?.description ?? "cover image title alt",
              style: {
                objectFit: "contain",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export const UGCVideo = withErrorBoundary(UCGVideoPlayer, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`UCGVideo failed to load. ${error}`)
  },
})
